/* ============================================================
   AddressInput — CB-3 (31de9d9f): text input with DaData suggest
   (GET /api/v1/addresses/suggest, task-218). Debounced, stale-safe;
   suggestions are decorative — any failure leaves a plain input.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { AddressSuggestion } from '../types/address';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const DEBOUNCE_MS = 350;
const MIN_QUERY = 3;

export function AddressInput({ value, onChange, placeholder, style, disabled }: Props) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // 3830fe0d: the backend marks a missing DaData key in-band — show a one-line
  // hint so "no autocomplete" is explained in-product instead of silently blank.
  const [disabledHint, setDisabledHint] = useState(false);
  const lastQuery = useRef<string>('');
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced suggest lookup; drop stale responses by query text.
  useEffect(() => {
    const query = value.trim();
    if (query.length < MIN_QUERY) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      setDisabledHint(false);
      lastQuery.current = '';
      return;
    }

    const timer = setTimeout(() => {
      if (lastQuery.current === query) return;
      lastQuery.current = query;
      setLoading(true);
      api
        .suggestAddresses(query, 8)
        .then((rows) => {
          setDisabledHint(false);
          setSuggestions(rows);
          setOpen(rows.length > 0);
        })
        .catch((err) => {
          setDisabledHint(err instanceof Error && err.message.includes('отключены'));
          setSuggestions([]);
        }) // decorative — never surface errors
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value]);

  useEffect(
    () => () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    },
    [],
  );

  const closeSoon = () => {
    blurTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const pick = (s: AddressSuggestion) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    onChange(s.value);
    setSuggestions([]);
    setOpen(false);
    lastQuery.current = s.value;
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder ?? 'Адрес'}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onBlur={closeSoon}
        autoComplete="off"
        style={{
          width: '100%',
          height: 44,
          padding: '0 var(--space-md)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-separator)',
          background: 'var(--color-surface)',
          fontSize: 'var(--fs-body)',
        }}
      />
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 48,
            left: 0,
            right: 0,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-separator)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            zIndex: 50,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {loading && suggestions.length === 0 && (
            <p style={{ padding: 'var(--space-sm) var(--space-md)', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Поиск адреса…
            </p>
          )}
          {suggestions.map((s) => (
            <button
              key={s.value}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // beat the input blur
                pick(s);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--space-sm) var(--space-md)',
                background: 'none',
                border: 'none',
                borderBottom: '1px solid var(--color-separator)',
                cursor: 'pointer',
                fontSize: 'var(--fs-body)',
                color: 'var(--color-text-primary)',
              }}
            >
              {s.value}
            </button>
          ))}
        </div>
      )}
      {disabledHint && !open && (
        <p
          style={{
            margin: 'var(--space-xs) 0 0',
            fontSize: 'var(--fs-caption)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Автодополнение адресов отключено на сервере
        </p>
      )}
    </div>
  );
}
